import React, { useState } from "react";
import { ShieldCheck, Loader2, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

const ID_TYPES = ["Passport", "Driver's licence", "State ID", "Green card"];

export const RemitKycCard = ({ status, note, onSubmitted }) => {
  const [f, setF] = useState({ legal_name: "", dob: "", address: "", id_type: "Passport", id_number: "" });
  const [busy, setBusy] = useState(false);

  if (status === "pending") {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5" data-testid="remit-kyc-pending">
        <div className="flex items-center gap-2 font-heading font-bold text-amber-900"><Clock className="h-4 w-4" /> Identity check in review</div>
        <p className="text-sm text-amber-800 mt-1">We&rsquo;re verifying your details — usually within a few hours. You can save recipients and get quotes meanwhile.</p>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.post("/remit/kyc", f); toast.success("Submitted for review"); onSubmitted?.(data); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const inp = "w-full bg-zinc-50 border border-black/10 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-primary";

  return (
    <form onSubmit={submit} className="bg-white border border-black/10 rounded-2xl p-5 shadow-sm space-y-3" data-testid="remit-kyc-form">
      <div className="flex items-center gap-2 font-heading font-bold"><ShieldCheck className="h-4 w-4 text-primary" /> Verify your identity to send money</div>
      <p className="text-sm text-zinc-500">Required once by law for money transfers. Your details are reviewed by the Zeeptel team and never shared.</p>
      {status === "rejected" && <p className="text-sm text-red-600 font-semibold" data-testid="remit-kyc-rejected">We couldn&rsquo;t verify your details{note ? `: ${note}` : ""}. Please check and resubmit.</p>}
      <input required value={f.legal_name} onChange={(e) => setF({ ...f, legal_name: e.target.value })} placeholder="Full legal name (as on ID)" className={inp} data-testid="kyc-legal-name" />
      <div className="grid grid-cols-2 gap-3">
        <input required type="date" value={f.dob} onChange={(e) => setF({ ...f, dob: e.target.value })} className={inp} data-testid="kyc-dob" />
        <select value={f.id_type} onChange={(e) => setF({ ...f, id_type: e.target.value })} className={inp} data-testid="kyc-id-type">
          {ID_TYPES.map((t) => <option key={t}>{t}</option>)}
        </select>
      </div>
      <input required value={f.id_number} onChange={(e) => setF({ ...f, id_number: e.target.value })} placeholder="ID number" className={`${inp} font-mono`} data-testid="kyc-id-number" />
      <input required value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} placeholder="Home address (street, city, state, ZIP)" className={inp} data-testid="kyc-address" />
      <button type="submit" disabled={busy} className="w-full h-12 rounded-full bg-primary text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="kyc-submit">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Submit for verification
      </button>
    </form>
  );
};

export const TRANSFER_STATUS = {
  awaiting_payment: { label: "Awaiting payment", cls: "bg-zinc-100 text-zinc-600" },
  paid: { label: "Paid — starting payout", cls: "bg-blue-50 text-blue-700" },
  processing: { label: "On its way", cls: "bg-amber-50 text-amber-700" },
  delivered: { label: "Delivered", cls: "bg-emerald-50 text-emerald-700" },
  payout_failed: { label: "Payout failed — we'll refund", cls: "bg-red-50 text-red-700" },
  refunded: { label: "Refunded", cls: "bg-zinc-100 text-zinc-600" },
};

export const StatusBadge = ({ status }) => {
  const s = TRANSFER_STATUS[status] || { label: status, cls: "bg-zinc-100 text-zinc-600" };
  return <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${s.cls}`} data-testid={`transfer-status-${status}`}>{s.label}</span>;
};
